import { type Observable, combineLatest, from, merge, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { isArdoqError } from '@ardoq/common-helpers';
import type {
  APIComponentAttributes,
  APIReferenceAttributes,
  APIScopeData,
  ArdoqId,
  RenderComponentEntry,
  RenderReferenceEntry,
  RenderResponse,
} from '@ardoq/api-types';
import { transformationsApi } from '@ardoq/api';
import { action$, extractPayload, ofType, spreadMap } from '@ardoq/rxbeach';
import {
  activeFutureStateIds$,
  type ActiveFutureStateIds,
} from './futureStatePage$';
import {
  futureStateClaimGroupsChanged,
  futureStateProposalsChanged,
} from './futureStateProposals$';
import { futureStateDraftClaims$ } from './futureStateDraftClaims$';
import {
  draftClaimIdsOf,
  draftsForFutureState,
  mergeDrafts,
  toRenderRequest,
  type FutureStateDraftClaimsState,
  type FutureStateDrafts,
  type MergedClaims,
  type PersistedClaims,
} from './futureStateDrafts';
import {
  futureStateSelection$,
  toEndpointsByReferenceId,
  toScopeComponentIds,
  type FutureStateSelection,
  type LoadedFutureStateSelection,
} from './futureStateSelection$';
import { type ReferenceEndpoints } from './hiddenComponents';

type PendingRender = { kind: 'loading' } | { kind: 'error' };

type RenderBasis = { futureStateId: ArdoqId; renderedDraftVersion: number };

type EmptyRender = RenderBasis & { kind: 'empty' };

type ReadyRender = RenderBasis & { kind: 'ready'; response: RenderResponse };

type BaseRenderResult = PendingRender | EmptyRender | ReadyRender;

type ResultClaims = MergedClaims & {
  draftClaimIds: ReadonlySet<ArdoqId>;
  claimsScopeData?: APIScopeData;
};

type ClaimsRenderResult =
  | PendingRender
  | (EmptyRender & ResultClaims)
  | (ReadyRender & ResultClaims);

type SelectionScope = {
  scopeComponentIds: ReadonlySet<ArdoqId>;
  endpointsByReferenceId: Record<ArdoqId, ReferenceEndpoints>;
};

export type RenderResult =
  | PendingRender
  | (EmptyRender & ResultClaims)
  | (ReadyRender & SelectionScope & ResultClaims);

export type ClaimsData =
  | { kind: 'loading' }
  | { kind: 'error' }
  | (ActiveFutureStateIds &
      PersistedClaims & { kind: 'ready'; claimsScopeData?: APIScopeData });

const fetchClaims = (ids: ActiveFutureStateIds): Observable<ClaimsData> =>
  from(transformationsApi.fetchFutureStateClaims(ids)).pipe(
    map((result): ClaimsData =>
      isArdoqError(result)
        ? { kind: 'error' }
        : {
            ...ids,
            kind: 'ready',
            claims: result.claims,
            claimGroups: result.claimGroups ?? [],
            claimsScopeData: result.scopeData,
          }
    ),
    catchError(() => of<ClaimsData>({ kind: 'error' }))
  );

const changesOf = (
  changed: typeof futureStateProposalsChanged,
  { futureStateId }: ActiveFutureStateIds
) =>
  action$.pipe(
    ofType(changed),
    extractPayload(),
    filter(payload => payload.futureStateId === futureStateId)
  );

const proposalClaims$: Observable<ClaimsData> = activeFutureStateIds$.pipe(
  switchMap(ids =>
    changesOf(futureStateProposalsChanged, ids).pipe(
      startWith(null),
      switchMap(() =>
        fetchClaims(ids).pipe(startWith<ClaimsData>({ kind: 'loading' }))
      )
    )
  ),
  shareReplay({ bufferSize: 1, refCount: true })
);

const claimsFromGroupChange$: Observable<ClaimsData> =
  activeFutureStateIds$.pipe(
    switchMap(ids =>
      changesOf(futureStateClaimGroupsChanged, ids).pipe(
        switchMap(() => fetchClaims(ids)),
        filter(({ kind }) => kind === 'ready')
      )
    )
  );

export const futureStateClaimsData$: Observable<ClaimsData> = merge(
  proposalClaims$,
  claimsFromGroupChange$
).pipe(shareReplay({ bufferSize: 1, refCount: true }));

type RenderInputs = {
  claimsData: ClaimsData;
  drafts: FutureStateDrafts | null;
};

const toRenderInputs = ([claimsData, draftState]: [
  ClaimsData,
  FutureStateDraftClaimsState,
]): RenderInputs => ({
  claimsData,
  drafts:
    claimsData.kind === 'ready'
      ? draftsForFutureState(draftState, claimsData.futureStateId)
      : null,
});

const sameRenderInputs = (a: RenderInputs, b: RenderInputs): boolean =>
  a.claimsData === b.claimsData &&
  a.drafts?.latestDraftVersion === b.drafts?.latestDraftVersion;

const renderProposals = ({
  claimsData,
  drafts,
}: RenderInputs): Observable<BaseRenderResult> => {
  if (claimsData.kind !== 'ready') return of(claimsData);
  const { initiativeId, futureStateId, claims, claimGroups } = claimsData;
  const basis: RenderBasis = {
    futureStateId,
    renderedDraftVersion: drafts?.latestDraftVersion ?? 0,
  };
  const body = toRenderRequest(
    mergeDrafts({ persisted: { claims, claimGroups }, drafts })
  );
  if (claims.length === 0 && body.claims.length === 0) {
    return of<BaseRenderResult>({ ...basis, kind: 'empty' });
  }
  return from(
    transformationsApi.renderFutureState({ initiativeId, futureStateId, body })
  ).pipe(
    map((result): BaseRenderResult =>
      isArdoqError(result)
        ? { kind: 'error' }
        : { ...basis, kind: 'ready', response: result }
    ),
    catchError(() => of<BaseRenderResult>({ kind: 'error' })),
    startWith<BaseRenderResult>({ kind: 'loading' })
  );
};

const renderResult$: Observable<BaseRenderResult> = combineLatest([
  proposalClaims$,
  futureStateDraftClaims$,
]).pipe(
  map(toRenderInputs),
  distinctUntilChanged(sameRenderInputs),
  switchMap(renderProposals)
);

const withClaims = (
  render: BaseRenderResult,
  claimsData: ClaimsData,
  draftState: FutureStateDraftClaimsState
): ClaimsRenderResult => {
  if (render.kind === 'loading' || render.kind === 'error') return render;
  if (claimsData.kind !== 'ready') return claimsData;
  const { claims, claimGroups, claimsScopeData } = claimsData;
  const merged = mergeDrafts({
    persisted: { claims, claimGroups },
    drafts: draftsForFutureState(draftState, render.futureStateId),
  });
  return {
    ...render,
    claimsScopeData,
    ...merged,
    draftClaimIds: draftClaimIdsOf(merged.claims),
  };
};

const EMPTY_RESPONSE: RenderResponse = {
  components: {},
  references: {},
  evaluations: {},
  completion: { total: 0, satisfied: 0 },
  claimExecution: {
    status: 'ok',
    cycleClaims: [],
    conflictClaims: [],
    blockedClaims: [],
    sequence: [],
  },
};

const toSelectionComponentEntry = (
  component: APIComponentAttributes
): RenderComponentEntry => ({
  entity: component,
  state: 'exists',
  change: 'none',
  claimIds: [],
});

const toSelectionReferenceEntry = (
  reference: APIReferenceAttributes
): RenderReferenceEntry => ({
  entity: reference,
  state: 'exists',
  change: 'none',
  claimIds: [],
});

const augmentResponseWithSelection = (
  response: RenderResponse,
  { components, references }: LoadedFutureStateSelection
): RenderResponse => {
  const componentAdditions = components.filter(
    ({ _id }) => !response.components[_id]
  );
  const referenceAdditions = references.filter(
    ({ _id }) => !response.references[_id]
  );
  if (componentAdditions.length === 0 && referenceAdditions.length === 0) {
    return response;
  }
  return {
    ...response,
    components: {
      ...response.components,
      ...Object.fromEntries(
        componentAdditions.map(component => [
          component._id,
          toSelectionComponentEntry(component),
        ])
      ),
    },
    references: {
      ...response.references,
      ...Object.fromEntries(
        referenceAdditions.map(reference => [
          reference._id,
          toSelectionReferenceEntry(reference),
        ])
      ),
    },
  };
};

const toSelectionScope = (
  selection: LoadedFutureStateSelection
): SelectionScope => ({
  scopeComponentIds: toScopeComponentIds(selection),
  endpointsByReferenceId: toEndpointsByReferenceId(selection),
});

const isEmptySelection = (selection: LoadedFutureStateSelection): boolean =>
  selection.components.length === 0 && selection.references.length === 0;

const mergeSelection = (
  result: ClaimsRenderResult,
  selection: FutureStateSelection
): RenderResult => {
  if (selection.status === 'loading') return { kind: 'loading' };
  if (result.kind === 'ready') {
    return {
      ...result,
      ...toSelectionScope(selection),
      response: augmentResponseWithSelection(result.response, selection),
    };
  }
  if (result.kind !== 'empty' || isEmptySelection(selection)) return result;
  return {
    ...result,
    kind: 'ready',
    response: augmentResponseWithSelection(EMPTY_RESPONSE, selection),
    ...toSelectionScope(selection),
  };
};

/**
 * Merges the persisted component selection into a render. Selection is
 * decoupled from proposals, so selected components never appear in the render
 * response itself; this adds them for display only, leaving the page's change
 * list untouched. This allows users to interact with the selection nodes/edges.
 */
const augmentWithSelection = (
  render$: Observable<ClaimsRenderResult>
): Observable<RenderResult> =>
  combineLatest([render$, futureStateSelection$]).pipe(
    spreadMap(mergeSelection)
  );

export const futureStateRenderResult$: Observable<RenderResult> = combineLatest(
  [renderResult$, futureStateClaimsData$, futureStateDraftClaims$]
).pipe(
  spreadMap(withClaims),
  augmentWithSelection,
  shareReplay({ bufferSize: 1, refCount: true })
);
